import { IconifyIcon } from "@iconify/react";
import { Box, GlobalStyles } from "@mui/material";
import { alpha, useTheme } from "@mui/material/styles";
import { SnackbarKey, SnackbarProvider } from "notistack";
import { ReactNode, useRef } from "react";
import { SnackbarUtilsConfigurator } from "src/utils/SnackbarUtils";

import { ColorSchema } from "../theme/palette";
import IconButtonAnimate from "./animate/IconButtonAnimate";
import Iconify from "./Iconify";

// ----------------------------------------------------------------------

function SnackbarStyles() {
	const theme = useTheme();
	const isLight = theme.palette.mode === "light";

	return (
		<GlobalStyles
			styles={{
				"#root": {
					"& .SnackbarContent-root": {
						width: "100%",
						padding: theme.spacing(1),
						margin: theme.spacing(0.25, 0),
						boxShadow: theme.customShadows.z8,
						borderRadius: theme.shape.borderRadius,
						color: theme.palette.grey[isLight ? 0 : 800],
						backgroundColor: theme.palette.grey[isLight ? 900 : 0],
						"&.SnackbarItem-variantSuccess, &.SnackbarItem-variantError, &.SnackbarItem-variantWarning, &.SnackbarItem-variantInfo":
							{
								color: theme.palette.text.primary,
								backgroundColor: theme.palette.background.paper
							},
						[theme.breakpoints.up("md")]: {
							minWidth: 240
						}
					},
					"& .SnackbarItem-message": {
						padding: "0 !important",
						fontWeight: theme.typography.fontWeightMedium
					},
					"& .SnackbarItem-action": {
						marginRight: 0,
						color: theme.palette.action.active,
						"& svg": { width: 20, height: 20 }
					}
				}
			}}
		/>
	);
}

// ----------------------------------------------------------------------

type Props = {
	children: ReactNode;
};

export default function NotistackProvider({ children }: Props) {
	const notistackRef = useRef<any>(null);

	const onClose = (key: SnackbarKey) => () => {
		notistackRef.current.closeSnackbar(key);
	};

	return (
		<>
			<SnackbarStyles />

			<SnackbarProvider
				action={(key) => (
					<IconButtonAnimate onClick={onClose(key)} size='small' sx={{ p: 0.5 }}>
						<Iconify icon={"eva:close-fill"} />
					</IconButtonAnimate>
				)}
				anchorOrigin={{ vertical: "top", horizontal: "right" }}
				autoHideDuration={3000}
				dense
				iconVariant={{
					info: <SnackbarIcon color='info' icon={"eva:info-fill"} />,
					success: <SnackbarIcon color='success' icon={"eva:checkmark-circle-2-fill"} />,
					warning: <SnackbarIcon color='warning' icon={"eva:alert-triangle-fill"} />,
					error: <SnackbarIcon color='error' icon={"eva:alert-circle-fill"} />
				}}
				maxSnack={5} // Set default variant
				preventDuplicate
				ref={notistackRef}
				// With close as default
				variant='success'
			>
				<SnackbarUtilsConfigurator />
				{children}
			</SnackbarProvider>
		</>
	);
}

// ----------------------------------------------------------------------

type SnackbarIconProps = {
	icon: IconifyIcon | string;
	color: ColorSchema;
};

function SnackbarIcon({ icon, color }: SnackbarIconProps) {
	return (
		<Box
			component='span'
			sx={{
				mr: 1.5,
				width: 40,
				height: 40,
				display: "flex",
				borderRadius: 1.5,
				alignItems: "center",
				justifyContent: "center",
				color: `${color}.main`,
				bgcolor: (theme) => alpha(theme.palette[color].main, 0.16)
			}}
		>
			<Iconify height={24} icon={icon} width={24} />
		</Box>
	);
}
